import { toJS } from 'mobx';
import dynamic from 'next/dynamic';
import { StoreContext } from 'pages/_app';
import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

const FreshChatComponent = dynamic(() => import('react-freshchat'), {
  ssr: false, // render only on client
});

const FreshChat = () => {
  const store = useContext(StoreContext);
  const userStore = toJS(store?.userStore);
  const [isFreshChatLoaded, setIsFreshChatLoaded] = useState(false);
  const { currentUser, isFreshChatOpen } = userStore;

  useEffect(() => {
    const timeout = setTimeout(() => setIsFreshChatLoaded(true), 4000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!isFreshChatLoaded || !currentUser) return;

    let cleanup = () => {};
    let interval;

    const setupWidget = () => {
      if (!window.fcWidget) return false;

      const handleWidgetOpen = () => {
        if (window.fcWidget.user) {
          // Clear previous session
          window.fcWidget.user
            .clear()
            .then(() => {
              window.fcWidget.user.setProperties({
                externalId: currentUser?.attributes?.email,
                firstName: currentUser?.attributes?.first_name,
                lastName: currentUser?.attributes?.last_name,
                email: currentUser?.attributes?.email,
              });
            })
            .catch(() => {
              console.warn('⚠️ Failed to clear session');
            });
        } else {
          window.fcWidget.user.setProperties({
            externalId: currentUser?.attributes?.email,
            firstName: currentUser?.attributes?.first_name,
            lastName: currentUser?.attributes?.last_name,
            email: currentUser?.attributes?.email,
          });
        }
      };

      const handleWidgetClosed = () => {
        store.userStore.setFreshChatOpen(false);
      };

      window.fcWidget.on('widget:opened', handleWidgetOpen);
      window.fcWidget.on('widget:closed', handleWidgetClosed);

      cleanup = () => {
        window.fcWidget.off('widget:opened', handleWidgetOpen);
        window.fcWidget.off('widget:closed', handleWidgetClosed);
      };

      return true;
    };

    // Retry until fcWidget is ready
    interval = setInterval(() => {
      if (setupWidget()) clearInterval(interval);
    }, 500);

    // Open widget if MobX state is true
    if (isFreshChatOpen && window.fcWidget) {
      window.fcWidget.open();
    }

    return () => {
      clearInterval(interval);
      cleanup();
    };
  }, [isFreshChatLoaded, isFreshChatOpen, currentUser]);

  return (
    <>
      {(isFreshChatLoaded || isFreshChatOpen) && (
        <FreshChatComponent
          token={process.env.NEXT_PUBLIC_FRESH_CHAT_TOKEN}
          config={{
            headerProperty: {
              direction: 'rtr',
            },
          }}
        />
      )}
    </>
  );
};

export default observer(FreshChat);
